import { throttle } from '@lc/libutils';
function notify(listeners) {
    // eslint-disable-next-line unicorn/no-useless-spread
    for (const fn of [...listeners]) {
        try {
            fn();
        }
        catch (e) {
            console.error(e);
        }
    }
}
function listen(listeners, listener) {
    listeners.add(listener);
    return () => void listeners.delete(listener);
}
function combine(...listeners) {
    return () => notify(listeners);
}
export class LibmapState {
    #lib;
    get lib() {
        return this.#lib;
    }
    #data;
    get data() {
        return this.#data;
    }
    constructor(lib) {
        this.#lib = lib;
        this.#map = null;
        this.#data = new LibmapDataState(this);
    }
    #map;
    #mapListeners = new Set();
    get map() {
        return this.#map;
    }
    set map(value) {
        this.#map = value;
        notify(this.#mapListeners);
    }
    onMap(listener) {
        return listen(this.#mapListeners, listener);
    }
    static async fromLoader(loader) {
        return new LibmapState(await loader());
    }
    static bindLoader(loader) {
        return async () => LibmapState.fromLoader(loader);
    }
}
class LibmapDataState {
    #destroy = null;
    #attached = false;
    constructor(state) {
        if (!state.lib.onEvent)
            return;
        // eslint-disable-next-line unicorn/consistent-function-scoping
        let detach = () => void 0;
        const reattach = () => {
            if (this.#attached)
                detach();
            if (!state.map)
                return;
            this.#attached = true;
            const moveHandler = throttle((e) => {
                this.#bounds = e.bounds;
                notify(this.#boundsListeners);
            }, 500);
            detach = combine(state.lib.onEvent(state.map, 'zoomend', e => {
                this.#zoom = e.zoom;
                notify(this.#zoomListeners);
                this.#bounds = state.map?.getBounds();
                notify(this.#boundsListeners);
            }), state.lib.onEvent(state.map, 'move', moveHandler), state.lib.onEvent(state.map, 'moveend', moveHandler), state.lib.onEvent(state.map, 'pixelRatio', e => {
                this.#pixelRatio = e.value;
                notify(this.#pixelRatioListeners);
            }), () => void (this.#attached = false));
            const update = () => {
                const map = state.map;
                this.#zoom = map.getZoom();
                notify(this.#zoomListeners);
                this.#bounds = map.getBounds();
                notify(this.#boundsListeners);
                this.#pixelRatio = map.getPixelRatio();
                notify(this.#pixelRatioListeners);
            };
            {
                const map = state.map;
                map.onReady(() => {
                    if (map !== state.map)
                        return;
                    if (!this.#destroy)
                        return;
                    this.#ready = true;
                    notify(this.#readyListeners);
                    update();
                });
            }
            this.#ready = state.map.ready;
            notify(this.#readyListeners);
            if (this.#ready)
                update();
        };
        reattach();
        this.#destroy = combine(state.onMap(reattach), () => {
            this.#destroy = null;
        });
    }
    get destroyed() {
        return !this.#destroy;
    }
    get attached() {
        return this.#attached;
    }
    destroy() {
        this.#destroy?.();
    }
    #zoom = undefined;
    #zoomListeners = new Set();
    get zoom() {
        return this.#zoom;
    }
    onZoom(listener) {
        return listen(this.#zoomListeners, listener);
    }
    #bounds = undefined;
    #boundsListeners = new Set();
    get bounds() {
        return this.#bounds;
    }
    onBounds(listener) {
        return listen(this.#boundsListeners, listener);
    }
    #pixelRatio = 1;
    #pixelRatioListeners = new Set();
    get pixelRatio() {
        return this.#pixelRatio;
    }
    onPixelRatio(listener) {
        return listen(this.#pixelRatioListeners, listener);
    }
    #ready = false;
    #readyListeners = new Set();
    get ready() {
        return this.#ready;
    }
    onReady(listener) {
        return listen(this.#readyListeners, listener);
    }
}
//# sourceMappingURL=data:application/json;base64,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